/**
 * @file ClientTab.js
 * @description This file contains the implementation of the ClientTab component, which is responsible for rendering the client tab in the user interface.
 * @module components/clients/ClientTab
 */

import React, { useState, useEffect } from "react";
import ClientStatusDropdown from "../shared/asyncDropdowns/ClientStatusDropdown";
import CopyFromDropdown from "../shared/asyncDropdowns/CopyFromDropdown";
import { getText } from '../../i18n';

/**
 * Represents the ClientTab component.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data object containing client information.
 * @param {Function} props.onChange - The function to handle changes in client information.
 * @returns {JSX.Element} The rendered ClientTab component.
 */
const ClientTab = ({ data, onChange }) => {
  const [clientTypeId, setClientTypeId] = useState(data.clientTypeId || "");
  const [copyFrom, setCopyFrom] = useState(data.copyFrom || "");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientTypeId !== data.clientTypeId || copyFrom !== data.copyFrom) {
      const updatedData = {
        clientTypeId,
        copyFrom,
      };
      onChange(updatedData);
    }
  }, [clientTypeId, copyFrom, data.clientTypeId, data.copyFrom, onChange]);

  const handleClientTypeChange = (selectedClientTypeId) => {
    setClientTypeId(selectedClientTypeId);
    setCopyFrom("");
  };

  const handleCopyFromChange = (selectedCopyFrom) => {
    if (selectedCopyFrom && !clientTypeId) {
      setError("Please select a client type first");
    } else {
      setCopyFrom(selectedCopyFrom);
      setError(null);
    }
  };

  return (
    <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto pb-5">
      {error && <div className="error">{error}</div>}
      <div className="form-group flex-sm-wrap flex-lg-nowrap">
        <ClientStatusDropdown
          selectedClientStatus={clientTypeId}
          onClientStatusChange={handleClientTypeChange}
          inlineLabel={false}
          isRequired={true}
        />
      </div>
      <div className="form-group flex-sm-wrap flex-lg-nowrap">
        <CopyFromDropdown
          selectedType={copyFrom}
          clientTypeId={clientTypeId}
          label={getText('msg_645f22f94dae')}
          inlineLabel={false}
          onTypeChange={handleCopyFromChange}
          isRequired={true}
          disabled={!clientTypeId}
        />
      </div>
    </div>
  );
};

export default ClientTab;
